// ---- The reel-in minigame (Living Waters) ------------------------------------
// A DDR-style scrolling arrow lane: W/A/S/D arrows fall toward a hit line;
// press the matching key as each crosses. Hits reel the fish in, misses raise
// the tension meter — the same pure judge arithmetic the server runs
// (sim/fishing.ts applyArrowResult) mirrors here for DISPLAY; the server's
// copy is the verdict, arriving as the ordinary catch/escape resolution.
//
// Judging rules (must match the server's exactly-next contract):
//   - a press within ±hitWindow of the NEXT unjudged arrow, with the right
//     dir → HIT (judged immediately);
//   - a wrong-dir or badly-timed press is CONSUMED but judges nothing;
//   - an arrow expires unpressed past +hitWindow → MISS.
// So arrow `i` is judged exactly once, in order — `ev.onInput(i, hit)` streams
// straight into the `reelInput` wire intent.
//
// Import discipline: sim/fishing.ts + types.ts only — NO game.ts/net.ts/state
// imports, so the studio Skill-Sim driver mounts the LITERAL same module.
import {
  startReelJudge, applyArrowResult,
  type ReelPattern, type ReelTuning, type ReelJudge,
} from "../sim/fishing.ts";
import type { Minigame, ReelGameEvents } from "./types.ts";

const LANE_KEYS = ["w", "a", "s", "d"] as const;
const LANE_GLYPHS = ["▲", "◀", "▼", "▶"] as const;
const ALIAS: Record<string, number> = {
  w: 0, a: 1, s: 2, d: 3,
  ArrowUp: 0, ArrowLeft: 1, ArrowDown: 2, ArrowRight: 3,
};

const LANE_H = 240;      // px of visible approach above the hit line
const PX_PER_SEC = 150;  // arrow fall speed
const HIT_Y = LANE_H - 28; // the hit line's y inside a lane

export function makeReelMinigame(cfg: {
  pattern: ReelPattern;
  /** ± seconds a press counts (server-derived: tunable × box reelWindowMul). */
  hitWindow: number;
  /** Tension arithmetic for the DISPLAY judge (pass FISHING_TUNING / a sandbox copy). */
  tun: ReelTuning;
  ev: ReelGameEvents;
  /** Header pill above the lanes. Folded INTO the overlay (was a separate
   *  floating game prompt that overlapped the lanes); pass "" to omit it. */
  title?: string;
}): Minigame {
  const { pattern, hitWindow, tun, ev } = cfg;
  const title = cfg.title ?? "‼ FIGHT — match the arrows!";
  let root: HTMLElement | null = null;
  let tensionFill: HTMLElement | null = null;
  let progressEl: HTMLElement | null = null;
  let arrowEls: HTMLElement[] = [];
  let laneEls: HTMLElement[] = [];
  let judge: ReelJudge = startReelJudge(pattern, hitWindow);
  let elapsed = 0;
  let done = false;

  const flashLane = (lane: number, ok: boolean) => {
    const el = laneEls[lane];
    if (!el) return;
    el.style.background = ok ? "rgba(120,220,140,0.28)" : "rgba(230,90,80,0.28)";
    setTimeout(() => { el.style.background = "rgba(10,18,26,0.55)"; }, 120);
  };

  const judgeNext = (hit: boolean) => {
    const i = judge.idx;
    const evs = applyArrowResult(judge, hit, tun);
    const el = arrowEls[i];
    if (el) {
      el.style.opacity = "0.25";
      el.style.color = hit ? "#7ee29a" : "#e66a5a";
    }
    ev.onInput(i, hit);
    if (evs.length) done = true; // display-side resolution — input stops mattering
  };

  return {
    mount() {
      if (root) return;
      root = document.createElement("div");
      root.id = "minigame";
      root.style.cssText =
        "position:fixed;left:50%;bottom:120px;transform:translateX(-50%);z-index:60;" +
        "display:flex;flex-direction:column;align-items:center;gap:6px;pointer-events:none;" +
        "font-family:inherit;user-select:none;";

      const lanes = document.createElement("div");
      lanes.style.cssText = "display:flex;gap:8px;";
      laneEls = [];
      arrowEls = [];
      for (let lane = 0; lane < 4; lane++) {
        const laneEl = document.createElement("div");
        laneEl.style.cssText =
          `position:relative;width:52px;height:${LANE_H}px;overflow:hidden;border-radius:8px;` +
          "background:rgba(10,18,26,0.55);border:1px solid rgba(255,255,255,0.14);transition:background 80ms;";
        // the hit line + key label
        const line = document.createElement("div");
        line.style.cssText =
          `position:absolute;left:0;right:0;top:${HIT_Y}px;height:2px;background:rgba(255,255,255,0.6);`;
        const label = document.createElement("div");
        label.textContent = LANE_KEYS[lane]!.toUpperCase();
        label.style.cssText =
          `position:absolute;left:0;right:0;top:${HIT_Y + 4}px;text-align:center;` +
          "font-size:13px;font-weight:700;color:rgba(255,255,255,0.65);";
        laneEl.append(line, label);
        lanes.appendChild(laneEl);
        laneEls.push(laneEl);
      }
      // one element per arrow, parked in its lane; tick() moves them
      for (let i = 0; i < pattern.arrows.length; i++) {
        const a = pattern.arrows[i]!;
        const el = document.createElement("div");
        el.textContent = LANE_GLYPHS[a.dir]!;
        el.style.cssText =
          "position:absolute;left:0;right:0;text-align:center;font-size:26px;line-height:1;" +
          "color:#ffd97a;text-shadow:0 1px 3px rgba(0,0,0,0.7);will-change:transform;transform:translateY(-40px);";
        laneEls[a.dir]!.appendChild(el);
        arrowEls.push(el);
      }

      const tension = document.createElement("div");
      tension.style.cssText =
        "width:232px;height:8px;border-radius:4px;background:rgba(10,18,26,0.6);" +
        "border:1px solid rgba(255,255,255,0.14);overflow:hidden;";
      tensionFill = document.createElement("div");
      tensionFill.style.cssText =
        "height:100%;width:0%;background:linear-gradient(90deg,#e6b45a,#e66a5a);transition:width 90ms;";
      tension.appendChild(tensionFill);

      progressEl = document.createElement("div");
      progressEl.style.cssText = "font-size:12px;color:rgba(255,255,255,0.8);text-shadow:0 1px 2px rgba(0,0,0,0.8);";
      progressEl.textContent = `reel it in! 0 / ${pattern.arrows.length}`;

      // Title pill (the old floating prompt, now the overlay's own header — it
      // rides the top of the column so it can never overlap the lanes/labels).
      if (title) {
        const titleEl = document.createElement("div");
        titleEl.textContent = title;
        titleEl.style.cssText =
          "font:bold 15px ui-monospace,monospace;color:#ffd86a;white-space:nowrap;" +
          "background:rgba(0,0,0,0.67);padding:8px 16px;border-radius:20px;box-shadow:0 2px 10px #0006;";
        root.append(titleEl, lanes, tension, progressEl);
      } else {
        root.append(lanes, tension, progressEl);
      }
      document.body.appendChild(root);
      judge = startReelJudge(pattern, hitWindow);
      elapsed = 0;
      done = false;
    },

    tick(dt: number) {
      if (!root) return;
      elapsed += dt;
      // expire the next arrow(s) past the window → misses
      while (!done && judge.idx < pattern.arrows.length && elapsed > pattern.arrows[judge.idx]!.t + hitWindow) {
        flashLane(pattern.arrows[judge.idx]!.dir, false);
        judgeNext(false);
      }
      // scroll every unjudged arrow toward the hit line
      for (let i = judge.idx; i < pattern.arrows.length; i++) {
        const a = pattern.arrows[i]!;
        const y = HIT_Y - (a.t - elapsed) * PX_PER_SEC;
        arrowEls[i]!.style.transform = `translateY(${Math.round(y - 26)}px)`;
      }
      if (tensionFill) tensionFill.style.width = `${Math.min(100, judge.tension * 100)}%`;
      if (progressEl && !done) progressEl.textContent = `reel it in! ${judge.idx} / ${pattern.arrows.length}`;
      if (progressEl && done) progressEl.textContent = judge.tension >= 1 ? "it's breaking free…" : "almost in…";
    },

    key(e: KeyboardEvent): boolean {
      if (!root || done) return false;
      const dir = ALIAS[e.key.length === 1 ? e.key.toLowerCase() : e.key];
      if (dir === undefined) return false; // not ours (Esc, Space, …)
      const i = judge.idx;
      const a = pattern.arrows[i];
      if (a && a.dir === dir && Math.abs(elapsed - a.t) <= hitWindow) {
        flashLane(dir, true);
        judgeNext(true);
      } else {
        flashLane(dir, false); // wrong lane / bad timing: consumed, judges nothing
      }
      return true;
    },

    unmount() {
      root?.remove();
      root = null;
      tensionFill = null;
      progressEl = null;
      arrowEls = [];
      laneEls = [];
    },
  };
}
